import {SkeletonBox} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import React from 'react'

import styles from './ContentView.module.css'

/* <ContentView.Container> */
interface ContentContainerProps {
  children: ReactNode
  width?: 'small' | 'medium' | 'large'
  /*
   * Pass this prop when a ContentContainer is rendered within a PacerLayout component without a PacerLayout.Header
   * This will add top padding equivalent to the height of the PacerLayout.Header, to ensure that the content is
   * aligned correctly across tabs, and does not conflict with the global header content that appears on small screens.
   */
  noHeader?: boolean
}

function ContentContainer({children, width, noHeader}: ContentContainerProps) {
  const bannerElements: ReactNode[] = []
  const otherChildren: ReactNode[] = []

  const childrenArray = React.Children.toArray(children)
  for (const child of childrenArray) {
    if (React.isValidElement(child) && child.type === ContentBanner) {
      bannerElements.push(child)
    } else {
      otherChildren.push(child)
    }
  }

  return (
    <div className={styles.scrollContainer}>
      <div className={clsx(styles.contentContainer, {[styles.noHeader]: noHeader})}>
        <div
          className={clsx(styles.content, {
            [styles.small]: width === 'small',
            [styles.medium]: width === 'medium',
          })}
        >
          {bannerElements}
          {otherChildren}
        </div>
      </div>
    </div>
  )
}

/* <ContentView.Banner> */
interface ContentBannerProps {
  children: ReactNode
}

function ContentBanner({children}: ContentBannerProps) {
  return <div className={styles.banner}>{children}</div>
}

/* <ContentView.Header> */
interface ContentHeaderProps {
  children: ReactNode
}

function ContentHeader({children}: ContentHeaderProps) {
  return <div className={styles.serviceHeader}>{children}</div>
}

/* <ContentView.Section> */
interface ContentSectionProps {
  children: ReactNode
  className?: string
}

function ContentSection({children, className}: ContentSectionProps) {
  return <section className={clsx(styles.section, className)}>{children}</section>
}

/* <ContentView.Title> */

interface ContentTitleProps {
  children: ReactNode
}

function Title({children}: ContentTitleProps) {
  return <h1 className={styles.serviceTitle}>{children}</h1>
}

/* <ContentView.Description> */

interface ContentDescriptionProps {
  children: ReactNode
}

function Description({children}: ContentDescriptionProps) {
  return <p className={styles.serviceDescription}>{children}</p>
}

/* <ContentView.Actions> */

interface ContentActionsProps {
  children: ReactNode
}

function Actions({children}: ContentActionsProps) {
  return <div className={styles.serviceActions}>{children}</div>
}

/* <ContentView.Section.Title> */

interface SectionTitleProps {
  children: ReactNode
}

function SectionTitle({children}: SectionTitleProps) {
  return <h2 className={styles.sectionTitle}>{children}</h2>
}

/* <ContentView.Section.Grid.Empty> */

interface ContentGridEmptyProps {
  children: ReactNode
}

function SectionGridEmpty({children}: ContentGridEmptyProps) {
  return <div className={styles.sectionGridEmpty}>{children}</div>
}

/* <ContentView.Section.Grid> */

interface ContentGridProps {
  children?: ReactNode
  loading?: boolean
}

function SectionGrid({children, loading = false}: ContentGridProps) {
  if (loading) {
    return (
      <div className={styles.sectionGrid} data-testid="service-view-skeleton">
        {[...Array(3)].map((_, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <SkeletonBox key={index} className={styles.skeletonCard} />
        ))}
      </div>
    )
  }

  const childArray = React.Children.toArray(children)

  const emptyComponent = childArray.find(child => React.isValidElement(child) && child.type === SectionGridEmpty)
  const gridItems = childArray.filter(child => !(React.isValidElement(child) && child.type === SectionGridEmpty))

  // If we have no grid items but we have an empty component
  if (gridItems.length === 0 && emptyComponent) {
    return <>{emptyComponent}</>
  }

  return <div className={styles.sectionGrid}>{gridItems}</div>
}

SectionGrid.Empty = SectionGridEmpty
ContentSection.Title = SectionTitle
ContentSection.Grid = SectionGrid

interface ContentViewNamespace {
  Title: React.FC<{children: React.ReactNode}>
  Description: React.FC<{children: React.ReactNode}>
  Actions: React.FC<{children: React.ReactNode}>
  Container: typeof ContentContainer
  Banner: typeof ContentBanner
  Header: typeof ContentHeader
  Section: typeof ContentSection & {
    Title: React.FC<{children: React.ReactNode}>
    Grid: typeof SectionGrid & {
      Empty: React.FC<{children: React.ReactNode}>
    }
  }
  displayName?: string
}

const ContentViewObject: Omit<ContentViewNamespace, 'displayName'> = {
  Title,
  Description,
  Actions,
  Container: ContentContainer,
  Banner: ContentBanner,
  Header: ContentHeader,
  Section: ContentSection,
}

const ContentView = ContentViewObject as ContentViewNamespace

ContentView.displayName = 'ContentView'
ContentContainer.displayName = 'ContentView.Container'
ContentBanner.displayName = 'ContentView.Banner'
ContentHeader.displayName = 'ContentView.Header'
ContentSection.displayName = 'ContentView.Section'
Title.displayName = 'ContentView.Title'
Description.displayName = 'ContentView.Description'
Actions.displayName = 'ContentView.Actions'
SectionTitle.displayName = 'ContentView.Section.Title'
SectionGrid.displayName = 'ContentView.Section.Grid'
SectionGridEmpty.displayName = 'ContentView.Section.Grid.Empty'

export default ContentView
export {ContentBanner, ContentContainer, ContentHeader, ContentSection, ContentView}
